<template>
	<div class="forum-detail-page">
		<div class="forum-detail-inner">
			<nav class="forum-detail-nav">
				<button
					type="button"
					class="forum-back-btn"
					title="Back"
					aria-label="Back"
					@click="goBack"
				>
					<i class="fas fa-arrow-left"></i>
				</button>
				<span class="forum-back-label">Discussion</span>
			</nav>

			<!-- Skeleton OP card while the thread is loading. -->
			<article v-if="!thread && isLoadingThread" class="forum-thread-card forum-detail-skeleton">
				<header class="forum-thread-header">
					<SkeletonLine width="40px" height="40px" radius="50%" />
					<div class="forum-thread-headmeta">
						<SkeletonLine width="160px" height="12px" />
						<SkeletonLine width="100px" height="10px" margin-top="6px" />
					</div>
				</header>
				<SkeletonLine width="72%" height="22px" margin-top="14px" />
				<SkeletonLine width="100%" height="12px" margin-top="14px" />
				<SkeletonLine width="96%" height="12px" margin-top="6px" />
				<SkeletonLine width="88%" height="12px" margin-top="6px" />
				<SkeletonLine width="60%" height="12px" margin-top="6px" />
			</article>

			<!-- Not found -->
			<div v-else-if="!thread" class="forum-empty-state">
				<h4>Thread not found</h4>
				<p>It may have been deleted or you don't have access.</p>
				<button type="button" class="forum-btn forum-btn-primary" @click="goBack">
					Back to discussions
				</button>
			</div>

			<!-- Detail -->
			<template v-else>
				<!-- Re-enable lives on the board view's toolbar — informational only here. -->
				<div v-if="shouldShowReadOnlyBanner" class="forum-readonly-banner" role="status">
					<i class="fas fa-lock"></i>
					<span class="message">This forum is disabled. Existing posts stay visible, but new threads, replies, and likes are paused until it's re-enabled.</span>
				</div>

				<article class="forum-thread-card forum-thread-detail-card" :class="{ 'forum-pinned': thread.isPinned }">
					<header class="forum-thread-header">
						<div class="forum-avatar" :style="avatarStyle(thread)" :title="threadAuthorEmail">
							{{ avatarLetter(thread) }}
						</div>
						<div class="forum-thread-headmeta">
							<div class="forum-thread-author-row">
								<span class="forum-author">{{ threadAuthorName }}</span>
								<span v-if="threadAuthorHandle" class="forum-author-handle">@{{ threadAuthorHandle }}</span>
								<span v-if="thread.isPinned" class="forum-pinned-badge">
									<i class="fas fa-thumbtack"></i> Pinned
								</span>
							</div>
							<div class="forum-thread-sub">
								<span :title="thread.createdAt">{{ formatTime(thread.createdAt) }}</span>
								<span v-if="threadEdited" class="forum-sep">·</span>
								<span v-if="threadEdited" class="forum-edited" :title="`Edited ${thread.updatedAt}`">edited</span>
								<span v-if="thread.viewCount != null" class="forum-sep">·</span>
								<span v-if="thread.viewCount != null">
									<i class="far fa-eye"></i> {{ thread.viewCount }}
								</span>
							</div>
						</div>
					</header>

					<!-- Display mode -->
					<template v-if="!isEditingThread">
						<h2 class="forum-thread-title">{{ thread.title }}</h2>

						<div class="forum-thread-body rich-content" v-html="renderedThreadBody"></div>

						<div class="forum-thread-actions">
							<button
								type="button"
								class="forum-action"
								:class="{
									'forum-action-reacted':  thread.hasReacted,
									'reaction-bumping':        isThreadReactionBumping,
									'forum-action-locked': !isBoardActive || !thread.isReactionAllowed,
								}"
								:disabled="!isBoardActive || !thread.isReactionAllowed"
								:title="!isBoardActive
									? 'Forum is disabled — no new likes can be added.'
									: !thread.isReactionAllowed
										? 'Reactions are locked on this thread.'
										: (thread.hasReacted ? 'Unlike' : 'Like')"
								@click="onReactThread"
							>
								<i :class="thread.hasReacted ? 'fas fa-thumbs-up' : 'far fa-thumbs-up'"></i>
								<span class="forum-action-label">{{ thread.hasReacted ? "Liked" : "Like" }}</span>
								<span v-if="(thread.reactionCount || 0) > 0" class="forum-action-count">{{ thread.reactionCount }}</span>
							</button>

							<div class="forum-action forum-action-static">
								<i class="far fa-comment"></i>
								<span class="forum-action-label">{{ thread.commentCount || 0 }} {{ (thread.commentCount === 1) ? "comment" : "comments" }}</span>
							</div>

							<button
								v-if="canEditThread"
								type="button"
								class="forum-action"
								:class="{ 'forum-action-locked': !isBoardActive }"
								:disabled="!isBoardActive"
								:title="isBoardActive
									? 'Edit'
									: 'Forum is disabled — edits are paused until it is re-enabled.'"
								@click="startThreadEdit"
							>
								<i class="far fa-edit"></i>
								<span class="forum-action-label">Edit</span>
							</button>

							<button
								v-if="isModerator && isBoardActive"
								type="button"
								class="forum-action"
								:class="{ 'forum-action-pinned': thread.isPinned }"
								:title="thread.isPinned ? 'Unpin thread' : 'Pin thread'"
								@click="onTogglePinThread"
							>
								<i class="fas fa-thumbtack"></i>
								<span class="forum-action-label">{{ thread.isPinned ? "Unpin" : "Pin" }}</span>
							</button>

							<button
								v-if="isModerator && isBoardActive"
								type="button"
								class="forum-action"
								:class="{ 'forum-action-locked': !thread.isCommentAllowed }"
								:title="thread.isCommentAllowed ? 'Lock comments' : 'Unlock comments'"
								@click="onToggleCommentsLock"
							>
								<i :class="thread.isCommentAllowed ? 'fas fa-lock-open' : 'fas fa-lock'"></i>
								<span class="forum-action-label">{{ thread.isCommentAllowed ? "Lock comments" : "Unlock comments" }}</span>
							</button>

							<button
								v-if="isModerator && isBoardActive"
								type="button"
								class="forum-action"
								:class="{ 'forum-action-locked': !thread.isReactionAllowed }"
								:title="thread.isReactionAllowed ? 'Disable reactions' : 'Enable reactions'"
								@click="onToggleReactionsLock"
							>
								<i :class="thread.isReactionAllowed ? 'far fa-thumbs-up' : 'fas fa-ban'"></i>
								<span class="forum-action-label">{{ thread.isReactionAllowed ? "Lock likes" : "Unlock likes" }}</span>
							</button>


							<button
								v-if="canDeleteThread"
								type="button"
								class="forum-action forum-action-danger"
								title="Delete thread"
								@click="onDeleteThread"
							>
								<i class="far fa-trash-alt"></i>
								<span class="forum-action-label">Delete</span>
							</button>
						</div>
					</template>

					<div v-else class="forum-thread-edit">
						<input
							v-model="editTitle"
							type="text"
							class="forum-thread-edit-title"
							placeholder="Thread title"
							maxlength="200"
						/>
						<MarkdownEditor
							ref="threadEditEditor"
							:key="threadEditKey"
							:autofocus="false"
							:compact="true"
							:show-actions="false"
							:allow-mentions="mentionsEnabled"
							:search-users="debouncedMentionSearch"
							:upload-entity="entityType.Thread"
							:allow-attachments="threadAttachmentPolicy.isAllowed"
							:allowed-attachment-types="threadAttachmentPolicy.allowedTypes"
							placeholder="Edit thread body…"
							send-label="Save"
							@update="onThreadEditUpdate"
						/>
						<div class="forum-thread-edit-bar">
							<button
								type="button"
								class="forum-btn forum-btn-ghost forum-btn-sm"
								:disabled="isSavingThread"
								@click="cancelThreadEdit"
							>Cancel</button>
							<button
								type="button"
								class="forum-btn forum-btn-primary forum-btn-sm"
								:disabled="!canSaveThreadEdit || isSavingThread"
								@click="saveThreadEdit"
							>{{ isSavingThread ? "Saving…" : "Save" }}</button>
						</div>
					</div>
				</article>

				<section v-if="isBoardActive && thread.isCommentAllowed" class="forum-comment-composer">
					<div class="forum-composer-body">
						<MarkdownEditor
							ref="composer"
							:key="composerKey"
							:autofocus="false"
							:compact="true"
							:show-actions="false"
							:allow-mentions="mentionsEnabled"
							:search-users="debouncedMentionSearch"
							:upload-entity="entityType.Comment"
							:allow-attachments="commentAttachmentPolicy.isAllowed"
							:allowed-attachment-types="commentAttachmentPolicy.allowedTypes"
							placeholder="Write a comment… drop files to attach"
							send-label="Post"
							@update="onComposerUpdate"
						/>
						<div class="forum-composer-bar">
							<div class="forum-composer-spacer"></div>
							<button
								type="button"
								class="forum-btn forum-btn-primary forum-btn-sm"
								:disabled="!canPost || isPosting"
								@click="postComment"
							>
								{{ isPosting ? "Posting…" : "Post" }}
							</button>
						</div>
					</div>
				</section>
				<section v-else-if="isBoardActive" class="forum-locked-banner">
					<i class="fas fa-lock"></i>
					<span>Comments are locked on this thread.</span>
				</section>

				<section class="forum-comments-list">
					<template v-if="bucket.isLoading && bucket.comments.length === 0">
						<div v-for="i in 4" :key="'csk-' + i" class="forum-message-skel">
							<SkeletonLine width="36px" height="36px" radius="50%" />
							<div class="forum-message-skel-body">
								<SkeletonLine width="140px" height="11px" />
								<SkeletonLine width="92%"  height="11px" margin-top="8px" />
								<SkeletonLine width="78%"  height="11px" margin-top="6px" />
								<SkeletonLine width="64%"  height="11px" margin-top="6px" />
							</div>
						</div>
					</template>

					<div v-else-if="bucket.comments.length === 0" class="forum-empty-comments">
						No comments yet — be the first to respond.
					</div>

					<template v-else>
						<h3 class="forum-comments-heading">
							{{ thread.commentCount || bucket.comments.length }}
							{{ (thread.commentCount === 1 || bucket.comments.length === 1) ? "comment" : "comments" }}
						</h3>

						<CommentItem
							v-for="comment in bucket.comments"
							:key="comment.id"
							:comment="comment"
							:root-entity-id="thread.id"
							:root-entity-type="entityType.Thread"
							:product-variant-id="productVariantId"
							:is-comments-locked="!thread.isCommentAllowed"
							:is-reactions-locked="!thread.isReactionAllowed"
							:is-viewer-moderator="isModerator"
							:is-writable="isBoardActive"
							@react="onReactComment"
							@reply="onReplyToComment"
							@delete="onDeleteComment"
							@bubble-up-reply="onBubbleUpReply"
						/>

						<!-- Sentinel for IntersectionObserver-driven infinite scroll. -->
						<div
							v-if="bucket.nextPageToken"
							ref="sentinel"
							class="forum-infinite-sentinel"
						>
							<button
								v-if="!bucket.isLoading"
								type="button"
								class="forum-link-btn"
								@click="loadMoreComments"
							>
								Load more comments
							</button>
							<div v-else class="forum-spinner forum-spinner-sm"></div>
						</div>
					</template>
				</section>
			</template>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import CommentItem from "../components/Forum/CommentItem.vue";
import MarkdownEditor from "../components/Forum/Editor/MarkdownEditor.vue";
import SkeletonLine from "../components/Shared/SkeletonLine.vue";
import { UserType } from "../constants/user";
import { AVATAR_COLORS, BoardScope, BoardStatus, MENTIONS_ENABLED } from "../constants/forum";
import { EntityType } from "../constants/entity";
import { markdownToHTML } from "../utils/markdown";
import { registerAttachments } from "../utils/attachment-registry";
import { resolveAttachmentPolicy } from "../utils/attachment-utility";
import { createMentionSearch } from "../utils/mention-search";

export default {
	name: "ForumThreadDetailView",
	components: { CommentItem, MarkdownEditor, SkeletonLine },
	data() {
		return {
			isLoadingThread: false,
			composerPayload: null,
			composerKey: Date.now(),
			isPosting: false,
			observer: null,
			isThreadReactionBumping: false,
			isEditingThread: false,
			editTitle: "",
			editThreadPayload: null,
			threadEditKey: 0,
			isSavingThread: false,
			entityType: EntityType,
			mentionsEnabled: MENTIONS_ENABLED,
			debouncedMentionSearch: createMentionSearch({ productVariantId: null }),
		};
	},
	computed: {
		...mapGetters("forum", ["threadById", "boardForKey"]),
		...mapGetters("interactions", ["commentsForEntity"]),
		...mapState("user", ["user"]),
		...mapState("appState", ["systemParameters"]),
		threadAttachmentPolicy() {
			return resolveAttachmentPolicy(this.systemParameters, EntityType.Thread);
		},
		commentAttachmentPolicy() {
			return resolveAttachmentPolicy(this.systemParameters, EntityType.Comment);
		},
		threadId() {
			return Number(this.$route.params.id);
		},
		thread() {
			return this.threadById(this.threadId);
		},
		bucket() {
			return this.commentsForEntity(EntityType.Thread, this.threadId);
		},
		productVariantId() {
			if (!this.thread) return null;
			const scope = (this.thread.boardScope || "").toLowerCase();
			if (scope !== BoardScope.ProductVariant) return null;
			const entityId = this.thread.boardEntityID;
			return entityId != null ? Number(entityId) : null;
		},
		threadAuthor() {
			return this.thread?.author || null;
		},
		threadAuthorName() {
			const author = this.threadAuthor;
			if (!author) return "Anonymous";
			const firstName = (author.firstName || "").trim();
			const lastName  = (author.lastName  || "").trim();
			if (firstName || lastName) return `${firstName} ${lastName}`.trim();
			if (author.username) return author.username;
			return "Anonymous";
		},
		threadAuthorHandle() {
			const author = this.threadAuthor;
			if (!author) return "";
			const hasFullName = (author.firstName || "").trim() || (author.lastName || "").trim();
			return hasFullName && author.username ? author.username : "";
		},
		threadAuthorEmail() {
			return this.threadAuthor?.email || "";
		},
		threadEdited() {
			if (!this.thread?.updatedAt || !this.thread?.createdAt) return false;
			return new Date(this.thread.updatedAt).getTime() > new Date(this.thread.createdAt).getTime() + 1000;
		},
		renderedThreadBody() {
			registerAttachments(this.thread?.attachments || []);
			return markdownToHTML(this.thread?.body || "");
		},
		canPost() {
			if (!this.composerPayload) return false;
			const plainText = (this.composerPayload.text || "").trim();
			const markdown  = (this.composerPayload.markdown || "").trim();
			return plainText.length > 0 || markdown.length > 0;
		},
		canEditThread() {
			return !!this.user && !!this.thread && this.isSameId(this.thread.createdBy, this.user.id);
		},
		canDeleteThread() {
			if (!this.user || !this.thread) return false;

			if (this.isModerator) return true;

			if (this.isSameId(this.thread.createdBy, this.user.id)) return this.isBoardActive;
			return false;
		},
		isModerator() {
			const userType = this.user && this.user.type;
			if (this.equalsIgnoreCase(userType, UserType.MANAGEMENT)) return true;

			if (this.equalsIgnoreCase(userType, UserType.TRAINER)) {
				return !!(this.thread
					&& this.equalsIgnoreCase(this.thread.boardScope, BoardScope.ProductVariant));
			}
			return false;
		},
		canSaveThreadEdit() {
			if (!this.editTitle || !this.editTitle.trim()) return false;
			if (!this.editThreadPayload) return false;
			const plainText = (this.editThreadPayload.text || "").trim();
			const markdown  = (this.editThreadPayload.markdown || "").trim();
			return plainText.length > 0 || markdown.length > 0;
		},
		boardLookupKey() {
			if (!this.thread || !this.thread.boardScope) return null;
			return this.thread.boardEntityID != null
				? `${this.thread.boardScope}:${this.thread.boardEntityID}`
				: this.thread.boardScope;
		},
		parentBoard() {
			const entry = this.boardLookupKey ? this.boardForKey(this.boardLookupKey) : null;
			return entry && entry.board ? entry.board : null;
		},
		isBoardActive() {
			return !!(this.parentBoard
				&& this.equalsIgnoreCase(this.parentBoard.status, BoardStatus.Active));
		},
		shouldShowReadOnlyBanner() {
			return !!this.thread && !!this.parentBoard && !this.isBoardActive;
		},
	},
	methods: {
		...mapActions("forum", ["fetchThreadById", "fetchBoard", "updateThread", "setThreadPinned", "deleteThread"]),

		isSameId(a, b) {
			if (a == null || b == null) return false;
			const numA = Number(a);
			const numB = Number(b);
			if (Number.isNaN(numA) || Number.isNaN(numB)) return false;
			return numA === numB;
		},
		...mapActions("interactions", [
			"setEntityPermissions",
			"fetchComments",
			"addComment",
			"deleteComment",
			"toggleReaction",
		]),
		equalsIgnoreCase(a, b) {
			if (a == null || b == null) return a === b;
			return String(a).toLowerCase() === String(b).toLowerCase();
		},
		goBack() {
			if (window.history.length > 1) { this.$router.go(-1); return; }

			const fallback = this.$route.meta && this.$route.meta.isAdmin
				? "ForumManagement"
				: "Forum";
			this.$router.push({ name: fallback });
		},
		avatarLetter(thread) {
			const author = thread?.author;
			if (!author) return "A";
			const firstName = (author.firstName || "").trim();
			const lastName  = (author.lastName  || "").trim();
			const nameSeed  = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (author.username || "A");
			return nameSeed.trim().charAt(0).toUpperCase() || "A";
		},
		avatarStyle(thread) {
			const colorIndex = ((thread.createdBy || 0) + (this.avatarLetter(thread).charCodeAt(0) || 0)) % AVATAR_COLORS.length;
			return { backgroundColor: AVATAR_COLORS[colorIndex] };
		},
		formatTime(iso) {
			if (!iso) return "";
			const timestamp   = new Date(iso);
			const diffMs      = Date.now() - timestamp.getTime();
			const diffMinutes = Math.floor(diffMs / 60000);
			if (diffMinutes < 1) return "just now";
			if (diffMinutes < 60) return `${diffMinutes}m`;
			const diffHours = Math.floor(diffMinutes / 60);
			if (diffHours < 24) return `${diffHours}h`;
			const diffDays = Math.floor(diffHours / 24);
			if (diffDays < 7) return `${diffDays}d`;
			return timestamp.toLocaleDateString();
		},

		showError(message, options = {}) {
			this.$toasted.error(message, { icon: "exclamation-triangle", theme: "outline", position: "bottom-center", singleton: true, duration: 3000, ...options });
		},
		showSuccess(message, options = {}) {
			this.$toasted.success(message, { icon: "check", theme: "outline", position: "bottom-center", duration: 3000, ...options });
		},
		async loadThread() {
			this.isLoadingThread = true;
			try {
				await this.fetchThreadById(this.threadId);

				if (this.thread) {
					this.fetchBoard({
						scope: this.thread.boardScope,
						entityId: this.thread.boardEntityID,
					}).catch(() => {});
				}
			} catch (err) {
				this.showError(err?.message || "Failed to load thread.");
			} finally {
				this.isLoadingThread = false;
			}
		},
		async loadComments(reload = true) {
			try {
				await this.fetchComments({
					entityType: EntityType.Thread,
					entityId: this.threadId,
					reload,
				});
			} catch (err) {
				this.showError(err?.message || "Failed to load comments.");
			}
		},
		async loadMoreComments() {
			if (!this.bucket.nextPageToken || this.bucket.isLoading) return;
			await this.loadComments(false);
		},
		onComposerUpdate(payload) {
			this.composerPayload = payload;
		},
		async postComment() {
			if (!this.canPost || this.isPosting) return;

			const payload = this.$refs.composer && this.$refs.composer.serialize
				? this.$refs.composer.serialize()
				: this.composerPayload;
			const attachmentIds = ((payload && payload.attachments) || [])
				.filter((attachment) => !attachment.uploading)
				.map((attachment) => attachment.id)
				.filter((attachmentId) => attachmentId != null);

			this.isPosting = true;
			try {
				await this.addComment({
					entityType: EntityType.Thread,
					entityId: this.threadId,
					body: (payload && payload.markdown) || "",
					attachmentIds,
				});
				this.composerPayload = null;
				this.composerKey = Date.now();
			} catch (postError) {
				this.showError(postError?.message || "Failed to post.");
			} finally {
				this.isPosting = false;
			}
		},
		startThreadEdit() {
			this.isEditingThread = true;
			this.editTitle = this.thread.title || "";
			this.editThreadPayload = null;
			this.threadEditKey = Date.now();
			// Wait for editor to mount, then seed body + register the
			// thread's attachments so [[file:id]] tokens resolve.
			this.$nextTick(() => {
				const editor = this.$refs.threadEditEditor;
				if (!editor) return;
				if (typeof editor.setMarkdown === "function") {
					editor.setMarkdown(this.thread.body || "", this.thread.attachments || []);
				}
			});
		},
		cancelThreadEdit() {
			this.isEditingThread = false;
			this.editTitle = "";
			this.editThreadPayload = null;
		},
		onThreadEditUpdate(payload) {
			this.editThreadPayload = payload;
		},
		async saveThreadEdit() {
			if (!this.canSaveThreadEdit || this.isSavingThread) return;

			const editor = this.$refs.threadEditEditor;
			const payload = editor && editor.serialize ? editor.serialize() : this.editThreadPayload;

			const attachmentIds = ((payload && payload.attachments) || [])
				.filter((attachment) => !attachment.uploading)
				.map((attachment) => attachment.id)
				.filter((attachmentId) => attachmentId != null);

			this.isSavingThread = true;
			try {
				await this.updateThread({
					id: this.thread.id,
					title: this.editTitle.trim(),
					body: (payload && payload.markdown) || "",
					attachmentIds,
				});
				this.isEditingThread = false;
				this.editThreadPayload = null;
			} catch (saveError) {
				this.showError(saveError?.message || "Failed to save.");
			} finally {
				this.isSavingThread = false;
			}
		},
		async onDeleteThread() {
			if (!this.canDeleteThread || !this.thread) return;
			const confirmed = await this.$bvModal.msgBoxConfirm(
				"Are you sure you want to delete this thread? All comments and replies under it will also be removed.",
				{
					title: "Delete thread?",
					okTitle: "Delete",
					okVariant: "danger",
					cancelTitle: "Cancel",
					centered: true,
				},
			);
			if (!confirmed) return;
			try {
				await this.deleteThread(this.thread.id);
				this.showSuccess("Thread deleted.");
				this.goBack();
			} catch (err) {
				this.showError(err?.message || "Failed to delete thread.");
			}
		},
		async onTogglePinThread() {
			const nextPinned = !this.thread.isPinned;
			try {
				await this.setThreadPinned({ id: this.thread.id, pinned: nextPinned });
				this.showSuccess(nextPinned ? "Pinned." : "Unpinned.");
			} catch (err) {
				this.showError(err?.message || "Failed to toggle pin.");
			}
		},
		async onToggleCommentsLock() {
			const next = !this.thread.isCommentAllowed;
			try {
				await this.setEntityPermissions({
					entityType: EntityType.Thread,
					entityId: this.thread.id,
					isCommentAllowed: next,
					isReactionAllowed: this.thread.isReactionAllowed,
				});

				await this.fetchThreadById(this.thread.id);
				this.showSuccess(next ? "Comments unlocked." : "Comments locked.");
			} catch (err) {
				this.showError(err?.message || "Failed to toggle lock.");
			}
		},
		async onToggleReactionsLock() {
			const next = !this.thread.isReactionAllowed;
			try {
				await this.setEntityPermissions({
					entityType: EntityType.Thread,
					entityId: this.thread.id,
					isCommentAllowed: this.thread.isCommentAllowed,
					isReactionAllowed: next,
				});
				await this.fetchThreadById(this.thread.id);
				this.showSuccess(next ? "Likes enabled." : "Likes disabled.");
			} catch (err) {
				this.showError(err?.message || "Failed to toggle likes.");
			}
		},
		async onReactThread() {
			if (!this.thread.isReactionAllowed) return;
			if (!this.thread.hasReacted) {
				this.isThreadReactionBumping = true;
				setTimeout(() => { this.isThreadReactionBumping = false; }, 600);
			}
			try {
				await this.toggleReaction({ entityType: EntityType.Thread, entityId: this.thread.id });
			} catch (err) {
				this.showError(err?.message || "Failed to toggle like.");
			}
		},
		async onReactComment(comment) {
			try {
				await this.toggleReaction({ entityType: EntityType.Comment, entityId: comment.id });
			} catch (err) {
				this.showError(err?.message || "Failed to toggle like.");
			}
		},
		onBubbleUpReply({ author }) {
			if (!author || author.id == null) return;
			const fullName = [author.firstName, author.lastName]
				.filter(Boolean).join(" ").trim() || author.username || "user";
			this.$nextTick(() => {
				const editor = this.$refs.composer;
				if (!editor) return;
				if (typeof editor.clear === "function") editor.clear();
				if (typeof editor.insertMention === "function") {
					editor.insertMention({ id: author.id, label: fullName, kind: "user" });
				}
				if (typeof editor.focus === "function") editor.focus();
				const el = this.$el && this.$el.querySelector
					? this.$el.querySelector(".forum-comment-composer")
					: null;
				if (el && typeof el.scrollIntoView === "function") {
					el.scrollIntoView({ behavior: "smooth", block: "center" });
				}
			});
		},
		async onReplyToComment({ parentComment, body, attachmentIds }) {
			try {
				await this.addComment({
					entityType: EntityType.Comment,
					entityId: parentComment.id,
					body,
					attachmentIds: attachmentIds || [],
					rootEntityType: EntityType.Thread,
					rootEntityId: this.threadId,
				});
			} catch (err) {
				this.showError(err?.message || "Failed to reply.");
			}
		},
		async onDeleteComment(comment) {
			try {
				await this.deleteComment({
					commentId: comment.id,
					rootEntityType: EntityType.Thread,
					rootEntityId: this.threadId,
				});
			} catch (err) {
				this.showError(err?.message || "Failed to delete.");
			}
		},
		setupObserver() {
			if (this.observer) {
				this.observer.disconnect();
				this.observer = null;
			}
			if (typeof IntersectionObserver === "undefined") return;
			this.$nextTick(() => {
				const el = this.$refs.sentinel;
				if (!el) return;
				this.observer = new IntersectionObserver((entries) => {
					if (entries.some((entry) => entry.isIntersecting)) {
						this.loadMoreComments();
					}
				}, { rootMargin: "200px 0px" });
				this.observer.observe(el);
			});
		},
	},
	watch: {
		"$route.params.id": {
			handler() {
				this.loadThread();
				this.loadComments(true).then(() => this.setupObserver());
			},
			immediate: false,
		},
		"bucket.comments.length"() {
			this.setupObserver();
		},
		productVariantId(newId) {
			this.debouncedMentionSearch = createMentionSearch({ productVariantId: newId });
		},
	},
	mounted() {
		this.loadThread();
		this.loadComments(true).then(() => this.setupObserver());
	},
	beforeDestroy() {
		if (this.observer) this.observer.disconnect();
	},
};
</script>

<style scoped>
.forum-detail-page {
	width: 100%;
	min-height: 100%;
	background: #f5f5f5;
	padding: 12px 16px 40px;
	font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
	color: #0f0f0f;
}

.forum-detail-inner {
	max-width: 960px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.forum-detail-nav {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	align-self: flex-start;
	margin-top: 12px;
}

.forum-back-btn {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: #ffffff;
	border: 1px solid #ececec;
	color: #0f0f0f;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 13px;
	font-family: inherit;
	transition: background-color 0.15s ease, border-color 0.15s ease,
	            box-shadow 0.15s ease, transform 0.1s ease;
}

.forum-back-btn:hover {
	background: #f2f2f2;
	border-color: #d0d0d0;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.forum-back-btn:active {
	transform: scale(0.95);
}

.forum-back-label {
	font-size: 13px;
	font-weight: 500;
	color: #606060;
	letter-spacing: 0.02em;
}

.forum-thread-card {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 20px 24px;
	border: 1px solid #ececec;
}

.forum-thread-card.forum-pinned {
	box-shadow: none;
}

.forum-thread-header {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
}

.forum-avatar {
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #ffffff;
	font-weight: 600;
	flex-shrink: 0;
	letter-spacing: -0.02em;
	width: 40px;
	height: 40px;
	font-size: 15px;
}

.forum-thread-headmeta {
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 1;
	line-height: 1.2;
}

.forum-thread-author-row {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.forum-author {
	font-size: 14px;
	font-weight: 500;
}

.forum-author-handle {
	font-size: 12px;
	color: #606060;
}

.forum-pinned-badge {
	background: rgba(0, 0, 0, 0.05);
	color: #606060;
	padding: 1px 7px;
	border-radius: 4px;
	font-size: 10px;
	font-weight: 500;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.forum-thread-sub {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	color: #606060;
	margin-top: 2px;
}

.forum-sep { color: #909090; }
.forum-edited { font-style: italic; color: #909090; }

/* Banner shown in place of the comment composer when comments are locked.
   Subtle, informative — not an alert. */
.forum-locked-banner {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 14px;
	margin: 12px 0 16px;
	border: 1px solid #e1e1e1;
	border-radius: 8px;
	background: #fafafa;
	color: #606060;
	font-size: 13px;
}
.forum-locked-banner i { color: #909090; }

/* Disabled-but-styled state for the OP Like button when reactions are
   locked. We do NOT desaturate the "liked" highlight — a previously-
   liked thread should still read as "you liked this", just not clickable. */
.forum-action.forum-action-locked,
.forum-action[disabled] {
	cursor: not-allowed;
	opacity: 0.7;
	pointer-events: auto;
}
.forum-action.forum-action-reacted.forum-action-locked,
.forum-action.forum-action-reacted[disabled] { opacity: 1; }

/* Destructive thread action (Delete) — red on hover, neutral otherwise so
   it doesn't dominate the action row. */
.forum-action.forum-action-danger { color: #909090; }
.forum-action.forum-action-danger:hover { color: var(--color-danger, #d3131a); }

.forum-thread-title {
	font-size: 22px;
	font-weight: 600;
	color: #0f0f0f;
	margin: 0 0 12px;
	line-height: 1.3;
	letter-spacing: -0.01em;
}

.forum-thread-body {
	color: #0f0f0f;
	font-size: 15px;
	line-height: 1.6;
	margin-bottom: 14px;
}

.forum-thread-body.rich-content p { margin: 0 0 10px 0; }
.forum-thread-body.rich-content p:last-child { margin-bottom: 0; }
.forum-thread-body.rich-content pre {
	background-color: #f8f8f8;
	padding: 14px 16px;
	border-radius: 8px;
	font-size: 13.5px;
	overflow-x: auto;
	margin: 10px 0;
	border: 1px solid #ececec;
}
.forum-thread-body.rich-content code {
	background-color: rgba(0, 0, 0, 0.05);
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 13.5px;
	font-family: "Roboto Mono", "SFMono-Regular", Consolas, monospace;
}
.forum-thread-body.rich-content a {
	color: var(--primary-color, #065fd4);
	text-decoration: none;
}
.forum-thread-body.rich-content a:hover { text-decoration: underline; }
.forum-thread-body.rich-content ul, .forum-thread-body.rich-content ol {
	padding-left: 22px;
	margin: 10px 0;
}
.forum-thread-body.rich-content li { margin-bottom: 4px; }
.forum-thread-body.rich-content blockquote {
	border-left: 3px solid #e0e0e0;
	padding-left: 14px;
	margin: 12px 0;
	color: #606060;
}
.forum-thread-body.rich-content strong { font-weight: 600; }
.forum-thread-body.rich-content img { max-width: 100%; border-radius: 8px; }
.forum-thread-body.rich-content .mention {
	background-color: rgba(3, 50, 77, 0.1);
	color: var(--secondary-color, #03324d);
	padding: 1px 6px;
	border-radius: 4px;
	font-weight: 500;
	white-space: nowrap;
}


.forum-thread-actions {
	display: flex;
	gap: 6px;
	padding-top: 10px;
	margin-top: 4px;
	border-top: 1px solid #f2f2f2;
	flex-wrap: wrap;
}

.forum-action {
	display: inline-flex;
	align-items: center;
	gap: 7px;
	padding: 7px 14px;
	background-color: transparent;
	border: none;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 500;
	color: #606060;
	cursor: pointer;
	transition: background-color 0.2s ease, color 0.2s ease;
	font-family: inherit;
}
.forum-action:hover { background-color: rgba(0, 0, 0, 0.05); color: #0f0f0f; }
.forum-action.forum-action-reacted {
	background-color: rgba(0, 0, 0, 0.05);
	color: var(--secondary-color, #0f0f0f);
}
.forum-action.forum-action-reacted i { color: var(--primary-color, #065fd4); }

/* Like pop on the OP card — same pattern as ThreadListItem. */
.forum-action i {
	display: inline-block;
	transition: color 0.18s ease, transform 0.18s ease;
}
.forum-action.reaction-bumping i {
	animation: forum-detail-reaction-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1);
	color: var(--primary-color, #ffd83f);
}
@keyframes forum-detail-reaction-pop {
	0%   { transform: scale(1); }
	18%  { transform: scale(0.82); }
	55%  { transform: scale(1.42); }
	100% { transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
	.forum-action.reaction-bumping i { animation: none; }
}
.forum-action.forum-action-static { cursor: default; }
.forum-action.forum-action-static:hover { background-color: transparent; color: #606060; }
.forum-action i { font-size: 14px; }
.forum-action-count {
	font-variant-numeric: tabular-nums;
	color: inherit;
	font-weight: 600;
	padding-left: 2px;
}

/* In-place thread edit composer */
.forum-thread-edit {
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 4px 0 0;
}
.forum-thread-edit-title {
	width: 100%;
	border: 1px solid #e1e1e1;
	border-radius: 8px;
	padding: 10px 14px;
	font-size: 18px;
	font-weight: 600;
	color: #242424;
	font-family: inherit;
	outline: none;
	background: #fafafa;
	transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.forum-thread-edit-title:focus {
	border-color: var(--secondary-color, #03324d);
	background: #ffffff;
	box-shadow: 0 0 0 3px rgba(3, 50, 77, 0.1);
}
.forum-thread-edit-bar {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 10px;
}

/* Composer — no avatar; the editor itself carries enough affordance. */
.forum-comment-composer {
	background: transparent;
}
.forum-composer-body {
	display: flex;
	flex-direction: column;
	gap: 8px;
}
.forum-composer-bar {
	display: flex;
	align-items: center;
	gap: 6px;
}
.forum-composer-spacer { flex: 1; }

.forum-comments-list {
	background: #ffffff;
	border: 1px solid #ececec;
	border-radius: 12px;
	padding: 6px 18px;
}

.forum-comments-heading {
	font-size: 13px;
	font-weight: 600;
	color: #242424;
	margin: 8px 0 0;
	padding: 0;
	letter-spacing: 0.01em;
}

.forum-empty-comments {
	text-align: center;
	padding: 28px 12px;
	color: #909090;
	font-size: 14px;
}

/* Compact spinner only for the "loading next page" sentinel — the
   initial-load states use skeletons instead. */
.forum-spinner-sm {
	width: 18px;
	height: 18px;
	border: 2px solid #ececec;
	border-top-color: var(--secondary-color, #03324d);
	border-radius: 50%;
	animation: forum-spin 0.7s linear infinite;
}
@keyframes forum-spin { to { transform: rotate(360deg); } }

/* Skeleton placeholders */
.forum-detail-skeleton { padding: 18px 20px; }

.forum-message-skel {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 12px 0;
	border-bottom: 1px solid #f0f0f0;
}
.forum-message-skel:last-child { border-bottom: none; }
.forum-message-skel-body { flex: 1; min-width: 0; }

.forum-infinite-sentinel {
	display: flex;
	justify-content: center;
	padding: 12px 0;
}

.forum-link-btn {
	background: transparent;
	border: none;
	color: var(--secondary-color, #0f0f0f);
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	padding: 6px 14px;
	border-radius: 999px;
	transition: background-color 0.2s ease;
}
.forum-link-btn:hover { background-color: rgba(0, 0, 0, 0.05); }

/* Empty state */
.forum-empty-state {
	background-color: #ffffff;
	border: 1px solid #ececec;
	border-radius: 16px;
	padding: 56px 24px 48px;
	text-align: center;
}
.forum-empty-state h4 {
	font-size: 18px;
	font-weight: 600;
	margin: 0 0 8px 0;
}
.forum-empty-state p {
	color: #606060;
	font-size: 14px;
	margin: 0 auto 24px;
	max-width: 380px;
	line-height: 1.5;
}

.forum-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 9px 20px;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 500;
	border: none;
	cursor: pointer;
	transition: background-color 0.2s ease, color 0.2s ease, transform 0.1s ease;
	font-family: inherit;
}
.forum-btn:active:not(:disabled) { transform: scale(0.98); }
.forum-btn-sm { padding: 7px 16px; font-size: 13px; }
.forum-btn-primary {
	background-color: var(--secondary-color, #0f0f0f);
	color: #ffffff;
}
.forum-btn-primary:hover:not(:disabled) { opacity: 0.9; }
.forum-btn-primary:disabled {
	background-color: #f2f2f2;
	color: #909090;
	cursor: not-allowed;
}

/* Mobile: tighter outer padding, smaller title, less card padding. */
@media (max-width: 600px) {
	.forum-detail-page { padding: 12px 8px 32px; }
	.forum-thread-card { padding: 14px 16px; border-radius: 10px; }
	.forum-thread-title { font-size: 18px; }
	.forum-thread-body { font-size: 14px; }
	.forum-comment-thread { padding: 12px 14px; border-radius: 10px; }
	.forum-back-label { display: none; }

	/* Tighten the action row so wrapped buttons don't double-stack with
	   excess gaps. Labels stay readable; only the padding shrinks. */
	.forum-thread-actions { gap: 4px; padding-top: 8px; }
	.forum-action { padding: 6px 10px; font-size: 12px; }
	.forum-action i { font-size: 13px; }

	/* Larger tap target for the back button row. */
	.forum-back-btn { width: 40px; height: 40px; }

	/* Comments list panel — drop side padding so the avatar+content fit
	   the narrow viewport without truncation. */
	.forum-comments-list { padding: 4px 10px; border-radius: 10px; }

	/* In-place edit composer: stack the action bar buttons full-width
	   so they're easier to thumb-tap on phones. */
	.forum-thread-edit-bar {
		flex-direction: row;
		gap: 8px;
	}
	.forum-thread-edit-bar .forum-btn { flex: 1; justify-content: center; }
	.forum-thread-edit-title { font-size: 16px; }
	.forum-locked-banner { padding: 10px 12px; font-size: 12.5px; }
	.forum-readonly-banner { flex-wrap: wrap; }
}

.forum-readonly-banner {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 14px;
	background: #fff8e1;
	border: 1px solid #f3d57a;
	border-radius: 8px;
	color: #5d4a00;
	font-size: 0.875rem;
	margin-bottom: 1rem;
}
.forum-readonly-banner i { color: #b07b00; font-size: 14px; }
.forum-readonly-banner .message { flex: 1; line-height: 1.4; }
</style>
